import { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { message, Modal } from 'antd';
import { useLazyQuery } from '@apollo/client/react';

import config from 'config';
import { noteVar } from 'apollo/reactiveVars';
import useAnalytics from 'hooks/useAnalytics';
import { CUSTOMER_SEARCH } from 'queries/customerSearch';
import { ANALYTICS_EVENTS } from 'utilities/constants';
import { COLOR_PALETTE, COLORS_CALABRIO } from 'utilities/styles';

export const AGENT_STATUS_COLORS = Object.freeze({
  AfterCallWork: {
    background: COLORS_CALABRIO.teal,
    foreground: COLOR_PALETTE.neutralLight0,
  },
  Available: {
    background: COLORS_CALABRIO.green,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  Break: {
    background: COLORS_CALABRIO.orange,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  Busy: {
    background: COLORS_CALABRIO.green,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  'Floor Walker': {
    background: COLORS_CALABRIO.peach,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  Lunch: {
    background: COLORS_CALABRIO.yellow,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  Meeting: {
    background: COLORS_CALABRIO.blue,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  MissedCallAgent: {
    background: COLORS_CALABRIO.red,
    foreground: COLOR_PALETTE.neutralLight0,
  },
  Offline: {
    background: COLORS_CALABRIO.red,
    foreground: COLOR_PALETTE.neutralLight0,
  },
  'Outbound Call': {
    background: COLORS_CALABRIO.purple,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  PendingBusy: {
    background: COLORS_CALABRIO.green,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  'Project Work': {
    background: COLORS_CALABRIO.pink,
    foreground: COLOR_PALETTE.neutralDark0,
  },
  ZenDesk: {
    background: COLORS_CALABRIO.gray,
    foreground: COLOR_PALETTE.neutralDark0,
  },
});

export const AWSConnectContext = createContext(null);

export const awsConnectConfig = {
  ccpUrl: `https://${config.AWS_CONNECT_INSTANCE}.awsapps.com/connect/ccp-v2`,
  loginPopup: true,
  loginUrl: `https://${config.AWS_CONNECT_INSTANCE}.awsapps.com/connect/login`,
  softphone: {
    allowFramedSoftphone: true,
  },
  ccpAckTimeout: 5000,
  ccpSynTimeout: 3000,
};

/**
 * TODO: We need to begin managing callInfo/contactId in browser storage
 * This information should be stored in reference to the customerId.
 * This is necessary because some agents have a tendency to open mission control
 * in multiple tabs but because AWS Connect manages call information at the
 * broswer level, a call received in one tab overwrites the contactId in all tabs
 */

const AWSConnectProvider = ({ children }) => {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();
  const [callInfo, setCallInfo] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  const [agentStatusColors, setAgentStatusColors] = useState(
    AGENT_STATUS_COLORS.Offline,
  );

  const [searchCustomer] = useLazyQuery(CUSTOMER_SEARCH, {
    fetchPolicy: 'network-only',
  });

  const handleBeforeUnload = useCallback((event) => {
    event.preventDefault();
    event.returnValue = '';
  });

  const initializeAWSConnect = useCallback((element) => {
    if (!window.connect) {
      return message.error(
        'There was a problem with AWS connect library, please refresh the page and try again.',
      );
    }

    setInitialized(true);

    const confirmDiscardNoteAndContinue = (goToNewCustomer) => {
      Modal.confirm({
        title: 'Incoming call',
        content: (
          <div>
            <p>Looks like there is an unsaved note from a previous customer.</p>
            <p>
              Would you like to continue editing the note or discard it and go
              to the new customer?
            </p>
          </div>
        ),
        okText: 'Discard note and go to new customer',
        onOk: () => {
          Modal.destroyAll();
          goToNewCustomer();
        },
        cancelText: 'Continue editing note',
        onCancel: () => {
          Modal.destroyAll();
        },
        width: 600,
      });
    };

    const handleCallConnection = async (call) => {
      setCallInfo(call);

      if (!call || !call.isInbound()) {
        return;
      }

      const callAttributes = call.getAttributes();
      const customerId = callAttributes?.screenPopValue?.value;

      const customerNotFound = () => {
        message.error(
          'Could not find customer from dialer. Please search for them manually.',
        );
        navigate('/customer-search');
      };

      if (!customerId) {
        customerNotFound();
        return;
      }

      try {
        const response = await searchCustomer({
          variables: { customerSearchRequestBody: { customerId } },
        });

        const customers = response?.data?.searchCustomer;
        if (!customers || customers.length === 0) {
          customerNotFound();
          return;
        }

        const customer = customers[0];
        const currentNote = (noteVar() || '').trim();

        const goToNewCustomer = () => {
          navigate(`/customers/${customer.customerId}`);
        };

        if (currentNote.length) {
          confirmDiscardNoteAndContinue(goToNewCustomer);
        } else {
          goToNewCustomer();
        }
      } catch (error) {
        customerNotFound();
      }
    };

    window.connect.core?.initCCP(element, awsConnectConfig);

    window.connect.contact((contact) => {
      contact.onConnecting(handleCallConnection);
      contact.onEnded(() => {
        setCallInfo(null);
      });
    });

    window.connect.agent((agent) => {
      agent.onStateChange((event) => {
        const status = event.newState;
        setAgentStatus(status);
        setAgentStatusColors(
          AGENT_STATUS_COLORS[status] || AGENT_STATUS_COLORS.Offline,
        );
      });
    });

    window.addEventListener('beforeunload', handleBeforeUnload);
    trackEvent(ANALYTICS_EVENTS.SOFTPHONE_INITIALIZED);
  }, []);

  useEffect(() => {
    return () => {
      window.connect?.core?.terminate();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <AWSConnectContext.Provider
      value={{
        initialized,
        initializeAWSConnect,
        agentStatus,
        agentStatusColors,
        callInfo,
      }}
    >
      {children}
    </AWSConnectContext.Provider>
  );
};

export default AWSConnectProvider;
